// src/pages/MassSending.tsx
import React, { useState, useEffect, useContext, ChangeEvent } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  IconButton,
  Collapse,
  Divider,
  LinearProgress,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  InputLabel,
} from '@mui/material';
import MDEditor, { commands } from '@uiw/react-md-editor';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';
import { getUserFriendlyError } from '../utils/errorHandler';
import AccountPickerMulti from '../components/AccountPickerMulti';
import type { AccountGroup } from '../components/AccountPickerMulti';
import { useFieldValidation } from '../hooks/useFieldValidation';
import type { FieldLimit, PairRule } from '../hooks/useFieldValidation';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import MailIcon from '@mui/icons-material/Mail';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import MicIcon from '@mui/icons-material/Mic';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

// ==== Границы полей: 1-в-1 из StartBroadcastCommand (+ TriggerReplyConfig / PinConfig) ====
// В модели у большинства полей нет ge/le (голые int с дефолтами) — ставим разумный
// минимум без выдуманного верхнего потолка. Жёсткие ge есть только у trigger-полей.
const MS_LIMITS: Record<string, FieldLimit> = {
  minDelay: { min: 0, integer: true },
  maxDelay: { min: 0, integer: true },
  messagesPerHour: { min: 1, integer: true, helper: 'Лимит на аккаунт' },
  dailyLimitPerAccount: { min: 1, integer: true },
  hourlyPause: { min: 0, integer: true, helper: '3600 = 1 час' },
  accountSwitchDelay: { min: 0, integer: true },
  maxRetries: { min: 0, integer: true },
  saveInterval: { min: 1, integer: true },
  maxParallelAccounts: { min: 1, integer: true },
  skipOnLongFlood: { min: 0, integer: true, helper: '0 = не пропускать. По умолчанию: 7400' },

  // autoDeleteTtl: на бэке снапится к {1д, 7д, 31д}. Здесь не ограничиваем жёстко.
  autoDeleteTtl: { min: 0, integer: true, optional: true, helper: '0 или пусто = без автоудаления' },

  // TriggerReplyConfig
  replyDelayMin: { min: 0, integer: true },
  replyDelayMax: { min: 0, integer: true },
  maxRepliesPerUser: { min: 0, integer: true, helper: '0 = без лимита' },

  // PinConfig.pin_delay_seconds (дефолт 1800, без ge/le)
  pinDelaySeconds: { min: 0, integer: true, optional: true },
};

const MS_PAIRS: PairRule[] = [
  { minKey: 'minDelay', maxKey: 'maxDelay' },
  { minKey: 'replyDelayMin', maxKey: 'replyDelayMax' },
];



interface Account {
  id: string;
  name: string;
  phone_number: string;
}

interface ActiveBroadcast {
  task_id: string;
  status: string;
  accounts_count: number;
  recipients_count: number;
  message_preview?: string;
  started_at?: string;
  progress: { sent: number; failed: number; total: number; percentage: number };
}

interface RecipientObject {
  id?: number;
  username?: string;
  phone?: string;
  source?: string;
}

const MassSending: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [activeBroadcasts, setActiveBroadcasts] = useState<ActiveBroadcast[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [tab, setTab] = useState<'setup' | 'active'>('setup');
  const [viewMode, setViewMode] = useState<'edit' | 'preview' | 'split'>('split');
  const [showEmoji, setShowEmoji] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showExtraFeatures, setShowExtraFeatures] = useState(false);

  const [form, setForm] = useState({
    accountNames: [] as string[],
    recipientInput: '',
    message: '',
    minDelay: '25',
    maxDelay: '45',
    messagesPerHour: '5',
    dailyLimitPerAccount: '20',
    hourlyPause: '3600',
    accountSwitchDelay: '180',
    maxRetries: '3',
    randomizeMessage: true,
    aiRandomizeMessage: false,
    useParallelAccounts: false,
    maxParallelAccounts: '5',
    skipOnLongFlood: '7400',
    saveInterval: '1',
    // Новые поля
    mode: 'message' as 'message' | 'auto_delete_open',
    triggerWords: '',
    fixedReply: '',
    useAiReply: false,
    aiSystemPrompt: '',
    aiTone: '',
    replyDelayMin: '5',
    replyDelayMax: '20',
    maxRepliesPerUser: '1',
    listenTimeout: '0',         // ← НОВОЕ ПОЛЕ: сколько слушать входящие после рассылки (сек)
    forwardTo: '',
    forwardAsForward: true,
    forwardAddCaption: true,
    autoDeleteTtl: '',
    pinDelaySeconds: ''
  });

  const [recipientsFile, setRecipientsFile] = useState<File | null>(null);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreviews, setMediaPreviews] = useState<string[]>([]);
  const [voiceFile, setVoiceFile] = useState<File | null>(null);

  const [recipientObjects, setRecipientObjects] = useState<RecipientObject[]>([]);

  const [objId, setObjId] = useState('');
  const [objUsername, setObjUsername] = useState('');
  const [objPhone, setObjPhone] = useState('');
  const [objSource, setObjSource] = useState('');

  const update = (key: keyof typeof form, value: any) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const validation = useFieldValidation({ limits: MS_LIMITS, pairs: MS_PAIRS });

  const handleRecipientsFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['csv', 'xlsx', 'xls', 'json'].includes(ext || '')) {
      setMessage({ text: 'Файл должен быть .csv, .xlsx, .xls или .json', type: 'error' });
      return;
    }
    setRecipientsFile(file);
    setForm(prev => ({ ...prev, recipientInput: '' }));
    setRecipientObjects([]);
    setMessage({ text: `Загружен файл: ${file.name}`, type: 'info' });
  };

  const handleMediaFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    if (newFiles.length + mediaFiles.length > 10) {
      setMessage({ text: 'Максимум 10 медиафайлов', type: 'error' });
      return;
    }

    const previews: string[] = [];
    newFiles.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          previews.push(reader.result as string);
          if (previews.length === newFiles.length) {
            setMediaPreviews(prev => [...prev, ...previews]);
          }
        };
        reader.readAsDataURL(file);
      }
    });

    setMediaFiles(prev => [...prev, ...newFiles]);
  };

  const removeMediaFile = (index: number) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
    setMediaPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const addEmoji = (emoji: any) => {
    update('message', form.message + emoji.native);
    setShowEmoji(false);
  };

  const addRecipientObject = () => {
    if (!objId.trim() && !objUsername.trim() && !objPhone.trim()) {
      setMessage({ text: 'Заполните хотя бы одно поле: ID, username или phone', type: 'error' });
      return;
    }

    const newObj: RecipientObject = {};

    if (objId.trim()) {
      const idNum = Number(objId.trim());
      if (isNaN(idNum) || idNum === 0) {
        setMessage({ text: 'ID должен быть целым числом ≠ 0', type: 'error' });
        return;
      }
      newObj.id = idNum;
    }

    if (objUsername.trim()) {
      let u = objUsername.trim().replace(/^@/, '');
      if (!/^[a-zA-Z0-9_]+$/.test(u)) {
        setMessage({ text: 'Username: только буквы, цифры, _', type: 'error' });
        return;
      }
      if (u.length < 5 || u.length > 32) {
        setMessage({ text: 'Username: 5–32 символа', type: 'error' });
        return;
      }
      newObj.username = u;
    }

    if (objPhone.trim()) {
      let p = objPhone.trim();
      if (!p.startsWith('+')) {
        setMessage({ text: 'Телефон должен начинаться с +', type: 'error' });
        return;
      }
      const digits = p.slice(1).replace(/[\s-]/g, '');
      if (!/^\d+$/.test(digits)) {
        setMessage({ text: 'Телефон: только цифры после +', type: 'error' });
        return;
      }
      if (p.length < 8 || p.length > 20) {
        setMessage({ text: 'Неверная длина телефона', type: 'error' });
        return;
      }
      newObj.phone = p;
    }

    if (objSource.trim()) newObj.source = objSource.trim();

    setRecipientObjects(prev => [...prev, newObj]);
    setObjId('');
    setObjUsername('');
    setObjPhone('');
    setObjSource('');
    setMessage({ text: 'Объект добавлен', type: 'success' });
  };

  useEffect(() => {
    if (!auth?.isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
    const interval = setInterval(fetchActive, 5000);
    return () => clearInterval(interval);
  }, [auth?.isAuthenticated, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [accRes, broadcastsRes] = await Promise.all([
        api.get('/accounts'),
        api.get('/mass_sending/active')
      ]);
      api.get('/account_groups').then(r => setGroups(r.data || [])).catch(() => {});
      setAccounts(accRes.data.accounts || []);
      setActiveBroadcasts(broadcastsRes.data || []);
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка загрузки', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActive = async () => {
    try {
      const res = await api.get('/mass_sending/active');
      setActiveBroadcasts(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const startBroadcast = async () => {
    if (!form.accountNames.length) {
      setMessage({ text: 'Выберите хотя бы один аккаунт', type: 'error' });
      return;
    }

    if (recipientsFile) {
      // файл имеет приоритет
    } else if (recipientObjects.length === 0 && !form.recipientInput.trim() && !voiceFile) {
      setMessage({ text: 'Укажите получателей или прикрепите голосовое', type: 'error' });
      return;
    }

    if (!validation.validateAll(form)) {
      setMessage({ text: 'Проверьте выделенные поля — есть ошибки валидации', type: 'error' });
      return;
    }

    const formData = new FormData();

    // Аккаунты
    form.accountNames.forEach(name => formData.append('account_names', name));

    // Получатели — ручной ввод
    if (!recipientsFile && form.recipientInput.trim()) {
      const parts = form.recipientInput.split(',').map(p => p.trim()).filter(Boolean);
      parts.forEach(part => {
        let value: any = part;

        if (/^-?\d+$/.test(part)) {
          value = parseInt(part, 10);
        } else if (part.startsWith('@')) {
          value = part.slice(1).trim();
        } else if (part.startsWith('+')) {
          value = part;
        } else if (part.startsWith('{') && part.endsWith('}')) {
          try {
            value = JSON.parse(part.replace(/([{,]\s*)([a-zA-Z_]+)(\s*:)/g, '$1"$2"$3'));
          } catch {}
        }

        formData.append('recipient_tg_ids', JSON.stringify(value));
      });
    }

    // Получатели — объекты
    recipientObjects.forEach(obj => {
      formData.append('recipient_tg_ids', JSON.stringify(obj));
    });

    // Основные параметры
    formData.append('message', form.message);
    formData.append('min_delay', form.minDelay);
    formData.append('max_delay', form.maxDelay);
    formData.append('messages_per_hour', form.messagesPerHour);
    formData.append('daily_limit_per_account', form.dailyLimitPerAccount);
    formData.append('hourly_pause', form.hourlyPause);
    formData.append('account_switch_delay', form.accountSwitchDelay);
    formData.append('max_retries', form.maxRetries);
    formData.append('randomize_message', String(form.randomizeMessage));
    formData.append('ai_randomize_message', String(form.aiRandomizeMessage));
    formData.append('use_parallel_accounts', String(form.useParallelAccounts));
    formData.append('max_parallel_accounts', form.maxParallelAccounts);
    formData.append('skip_on_long_flood', form.skipOnLongFlood || '0');
    formData.append('save_interval', form.saveInterval);

    // Новые поля — отправляем только если заполнены
    if (form.mode !== 'message') {
      formData.append('mode', form.mode);
    }

    if (form.triggerWords.trim() || form.fixedReply.trim() || form.useAiReply || form.listenTimeout.trim()) {
      const triggerConfig: any = {
        trigger_words: form.triggerWords.split(',').map(w => w.trim()).filter(Boolean),
        reply_delay_min: Number(form.replyDelayMin) || 5,
        reply_delay_max: Number(form.replyDelayMax) || 20,
        max_replies_per_user: Number(form.maxRepliesPerUser) || 1,
      };

      if (form.fixedReply.trim()) {
        triggerConfig.fixed_reply = form.fixedReply.trim();
      } else if (form.useAiReply) {
        triggerConfig.use_ai = true;
        if (form.aiSystemPrompt.trim()) triggerConfig.ai_system_prompt = form.aiSystemPrompt.trim();
        if (form.aiTone.trim()) triggerConfig.ai_tone = form.aiTone.trim();
      }

      // ← Добавляем listen_timeout
      if (form.listenTimeout.trim() && Number(form.listenTimeout) > 0) {
        triggerConfig.listen_timeout = Number(form.listenTimeout);
      }

      formData.append('trigger_reply', JSON.stringify(triggerConfig));
    }

    if (form.forwardTo.trim()) {
      const forwardConfig: any = {
        forward_to: form.forwardTo.trim(),
        as_forward: form.forwardAsForward,
        add_caption: form.forwardAddCaption,
      };
      formData.append('forward_config', JSON.stringify(forwardConfig));
    }

    if (form.autoDeleteTtl.trim() && Number(form.autoDeleteTtl) > 0) {
      formData.append('auto_delete_ttl', form.autoDeleteTtl.trim());
    }
    const pinSeconds = Number(form.pinDelaySeconds);

    if (!isNaN(pinSeconds) && pinSeconds > 0) {
      formData.append(
          'pin_config',
          JSON.stringify({
            pin_delay_seconds: pinSeconds
          })
      );
    }
    // Файлы
    if (recipientsFile) formData.append('recipients_file', recipientsFile);
    mediaFiles.forEach(f => formData.append('media_files', f));
    if (voiceFile) formData.append('voice_message', voiceFile);

    setIsLoading(true);
    setUploadProgress(0);

    try {
      await api.post('/mass_sending/start_broadcast', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
          setUploadProgress(percent);
        }
      });

      setMessage({ text: 'Рассылка успешно запущена!', type: 'success' });
      setTab('active');
      setForm(prev => ({ ...prev, recipientInput: '', message: '' }));
      setRecipientObjects([]);
      setRecipientsFile(null);
      setMediaFiles([]);
      setMediaPreviews([]);
      setVoiceFile(null);
    } catch (err: any) {
      const text = getUserFriendlyError(err, accounts, 'рассылку');
      setMessage({ text, type: 'error' });
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  const stopBroadcast = async (taskId: string) => {
    setIsLoading(true);
    try {
      await api.delete(`/mass_sending/stop/${taskId}`);
      setMessage({ text: 'Рассылка остановлена', type: 'info' });
      fetchActive();
    } catch (err: any) {
      const text = getUserFriendlyError(err, accounts, 'остановку рассылки');
      setMessage({ text, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const restoreBroadcast = async (taskId: string) => {
    setIsLoading(true);
    try {
      await api.get(`/mass_sending/restore/${taskId}`);
      setMessage({ text: 'Рассылка возобновлена!', type: 'success' });
      fetchActive();
    } catch (err: any) {
      const text = getUserFriendlyError(err, accounts, 'возобновление рассылки');
      setMessage({ text, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteBroadcast = async (taskId: string) => {
    if (!window.confirm('Удалить рассылку НАВСЕГДА? Это нельзя отменить.')) return;
    setIsLoading(true);
    try {
      await api.delete(`/mass_sending/remove/${taskId}`);
      setMessage({ text: 'Рассылка удалена навсегда', type: 'info' });
      fetchActive();
    } catch (err: any) {
      const text = getUserFriendlyError(err, accounts);
      setMessage({ text, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const getTabStyle = (current: 'setup' | 'active') => ({
    flex: 1,
    py: 2,
    borderRadius: 0,
    bgcolor: tab === current ? '#E0F2FE' : 'transparent',
    color: tab === current ? '#1E40AF' : '#6B7280',
    fontWeight: tab === current ? 600 : 400,
    '&:hover': { bgcolor: tab === current ? '#DBEAFE' : '#F3F4F6' }
  });

  if (isLoading && accounts.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#F8FAFC', borderRadius: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 2 }} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <SendIcon sx={{ fontSize: 40, color: '#1E40AF' }} />
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A8A', mb: 1 }}>
              Массовые рассылки
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label="Отправка любых сообщений тысячам пользователей" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Совместимость с базами ЦА из парсера" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Несколько стратегий привлечения внимания" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Встроенный автоответчик с ИИ" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
            </Box>
            <Typography sx={{ color: '#4B5563', fontSize: '0.95rem', lineHeight: 1.6 }}>
              Отправка персонализированных сообщений и голосовых тысячам пользователей с прогрессирующими лимитами и автоответчиком
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
        <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
          Как работают массовые рассылки?
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          Аккаунты рассылают ваше предложение тысячи получателей. Система автоматически распределяет нагрузку между аккаунтами с рандомными задержками.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Рандомизация текстовых сообщений</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Рандомизируйте текст вашего сообщения вручную или через ИИ
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Голосовые сообщения</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Прикрепите голосовое сообщение в формате .ogg — она отправится как обычное голосовое в Telegram.
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Антибан-система</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Случайные задержки, лимиты в час и в сутки, паузы между аккаунтами — подберите наиболее выгодную для вас стратегию рассылки.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Поддержка медиа и markdown-разметки</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Уникализируйте текст своего сообщения
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Отложенное закрепление сообщений</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Настройте отложенное закрепление сообщений для повторного обращения внимания на ваше сообщение
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Автоудаление сообщений</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Вместо текста сообщения - уведомление о начале общения для обращения внимания на профиль ваших аккаунтов.

            </Typography>
          </Box>
        </Box>

        <Typography sx={{ color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic' }}>
          Подходит для: рекламы, уведомлений, рассылок по базе, личных сообщений и голосовых приветствий
        </Typography>
         <Typography sx={{color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic'}}>
          Ознакомьтесь с документацией по автоматизации{' '}
          <Typography
              component="a"
              href="https://typhon.gitbook.io/docs/automatizations_telegram/mass_sendings"
              sx={{textDecoration: 'underline', cursor: 'pointer'}}
          >
            здесь
          </Typography>
          </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {uploadProgress > 0 && uploadProgress < 100 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2">Загрузка файлов...</Typography>
          <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1 }} />
          <Typography variant="caption" color="text.secondary">{uploadProgress}% загружено</Typography>
        </Paper>
      )}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex' }}>
          <Button onClick={() => setTab('setup')} sx={getTabStyle('setup')} startIcon={<SendIcon />}>
            Новая рассылка
          </Button>
          <Button onClick={() => setTab('active')} sx={getTabStyle('active')} startIcon={<StopIcon />}>
            Мои рассылки ({activeBroadcasts.length})
          </Button>
        </Box>

        <Box sx={{ p: 4 }}>
          {tab === 'setup' && (
            <Grid container spacing={3}>
              <Grid size={12}>
                <AccountPickerMulti
                  accounts={accounts}
                  selected={form.accountNames}
                  onChange={(names) => update('accountNames', names)}
                  groups={groups}
                />
              </Grid>

              <Grid size={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Получатели
                  <Tooltip title="Вводите через запятую или добавляйте объекты ниже">
                    <InfoIcon fontSize="small" sx={{ ml: 1, color: '#94A3B8' }} />
                  </Tooltip>
                </Typography>

                <TextField
                  label="ID / @username / +phone (через запятую)"
                  value={form.recipientInput}
                  onChange={e => update('recipientInput', e.target.value)}
                  fullWidth
                  disabled={!!recipientsFile}
                  placeholder="123456789, @durov, +79991234567, {'id': 123, 'source': @chan}"
                  helperText="Поддерживаются ID, @username, +phone, простые объекты"
                  multiline
                  rows={3}
                  sx={{ mb: 3 }}
                />

                <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: '#F8FAFC' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: '#1E40AF', fontWeight: 600 }}>
                    Добавить получателя как объект (словарь)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="ID (число)"
                        value={objId}
                        onChange={e => setObjId(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="123456789"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="@username"
                        value={objUsername}
                        onChange={e => setObjUsername(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="@durov"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="+phone"
                        value={objPhone}
                        onChange={e => setObjPhone(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="+79991234567"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Source (опционально)"
                        value={objSource}
                        onChange={e => setObjSource(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="@channel или https://t.me/..."
                      />
                    </Grid>
                    <Grid size={12}>
                      <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={addRecipientObject}
                        disabled={!objId.trim() && !objUsername.trim() && !objPhone.trim()}
                        fullWidth
                      >
                        Добавить в список
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>

                {recipientObjects.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Добавленные объекты-получатели ({recipientObjects.length})
                    </Typography>
                    <List dense>
                      {recipientObjects.map((obj, idx) => (
                        <ListItem
                          key={idx}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              onClick={() => setRecipientObjects(prev => prev.filter((_, i) => i !== idx))}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {obj.id && <Chip label={`ID: ${obj.id}`} size="small" color="primary" />}
                                {obj.username && <Chip label={`@${obj.username}`} size="small" color="secondary" />}
                                {obj.phone && <Chip label={obj.phone} size="small" />}
                                {obj.source && <Chip label={`source: ${obj.source}`} size="small" variant="outlined" />}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                <Box sx={{ mt: 2, p: 3, bgcolor: '#F8FAFC', borderRadius: 2, border: '2px dashed #CBD5E1' }}>
                  <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
                    Загрузить файл с ID (.csv, .xlsx, .json)
                    <input type="file" hidden accept=".csv,.xlsx,.xls,.json" onChange={handleRecipientsFile} />
                  </Button>

                  {recipientsFile && (
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachFileIcon color="primary" />
                      <Typography variant="body2">{recipientsFile.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({(recipientsFile.size / 1024 / 1024).toFixed(2)} МБ)
                      </Typography>
                      <IconButton size="small" onClick={() => setRecipientsFile(null)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    Файл имеет приоритет над ручным вводом и объектами
                  </Typography>
                </Box>
              </Grid>

              <Grid size={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Голосовое сообщение (опционально)
                  <Tooltip title="Отправляется вместо текста. Формат: .ogg с кодеком Opus">
                    <InfoIcon fontSize="small" sx={{ ml: 1, color: '#94A3B8' }} />
                  </Tooltip>
                </Typography>

                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<MicIcon />}
                  color={voiceFile ? 'success' : 'primary'}
                >
                  {voiceFile ? 'Голосовое прикреплено' : 'Прикрепить голосовое (.ogg)'}
                  <input
                    type="file"
                    hidden
                    accept="audio/ogg,audio/opus,.ogg"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (!file.type.includes('ogg') && !file.name.toLowerCase().endsWith('.ogg')) {
                        setMessage({ text: 'Голосовое должно быть в формате .ogg (Opus)', type: 'error' });
                        return;
                      }
                      if (file.size > 50 * 1024 * 1024) {
                        setMessage({ text: 'Голосовое не больше 50 МБ', type: 'error' });
                        return;
                      }
                      setVoiceFile(file);
                      setMessage({ text: `Голосовое прикреплено: ${file.name}`, type: 'info' });
                    }}
                  />
                </Button>

                {voiceFile && (
                  <Box sx={{ mt: 2, p: 3, bgcolor: '#F0FDF4', borderRadius: 2, border: '1px solid #86EFAC' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <MicIcon color="success" sx={{ fontSize: 32 }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {voiceFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(voiceFile.size / 1024 / 1024).toFixed(2)} МБ • OGG Opus
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setVoiceFile(null)}
                        sx={{ ml: 'auto' }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Как получить .ogg: запишите в Telegram → @voice2ogg_bot → скачайте
                </Typography>
              </Grid>

              <Grid size={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Медиафайлы (до 10)
                </Typography>
                <Button variant="outlined" component="label" startIcon={<ImageIcon />}>
                  Прикрепить фото / видео / документы ({mediaFiles.length}/10)
                  <input type="file" hidden multiple accept="image/*,video/*,.pdf,.doc,.docx" onChange={handleMediaFiles} />
                </Button>

                {mediaFiles.length > 0 && (
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    {mediaFiles.map((file, i) => (
                      <Grid size={{ xs: 6, sm: 4, md: 3 }} key={i}>
                        <Paper variant="outlined" sx={{ position: 'relative', overflow: 'hidden' }}>
                          {file.type.startsWith('image/') && mediaPreviews[i] ? (
                            <img src={mediaPreviews[i]} alt={file.name} style={{ width: '100%', height: 120, objectFit: 'cover' }} />
                          ) : file.type.startsWith('video/') ? (
                            <Box sx={{ height: 120, bgcolor: '#1E293B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <VideoFileIcon sx={{ fontSize: 48, color: 'white' }} />
                            </Box>
                          ) : (
                            <Box sx={{ height: 120, bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <DescriptionIcon sx={{ fontSize: 48, color: '#64748B' }} />
                            </Box>
                          )}
                          <Box sx={{ p: 1 }}>
                            <Typography variant="caption" noWrap>{file.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(file.size / 1024 / 1024).toFixed(1)} МБ
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }}
                            onClick={() => removeMediaFile(i)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>

              <Grid size={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} color="#1E40AF">
                    Сообщение
                  </Typography>
                  <Button variant="outlined" size="small" startIcon={<EmojiEmotionsIcon />} onClick={() => setShowEmoji(p => !p)}>
                    Эмодзи
                  </Button>
                  <Box sx={{ ml: 'auto', display: 'flex', bgcolor: '#f1f5f9', borderRadius: 2, p: 0.5 }}>
                    <Button size="small" variant={viewMode === 'edit' ? 'contained' : 'text'} onClick={() => setViewMode('edit')}>Редактировать</Button>
                    <Button size="small" variant={viewMode === 'preview' ? 'contained' : 'text'} onClick={() => setViewMode('preview')}>Превью</Button>
                    <Button size="small" variant={viewMode === 'split' ? 'contained' : 'text'} onClick={() => setViewMode('split')}>Рядом</Button>
                  </Box>
                </Box>

                {showEmoji && (
                  <Box sx={{ mb: 3, border: '1px solid #ddd', borderRadius: 2, overflow: 'hidden' }}>
                    <Picker data={data} onEmojiSelect={addEmoji} theme="light" previewPosition="none" />
                  </Box>
                )}

                <Box sx={{ border: '1px solid #d0d7de', borderRadius: 2, overflow: 'hidden' }}>
                  <MDEditor
                    value={form.message}
                    onChange={val => update('message', val || '')}
                    preview={viewMode === 'edit' ? 'edit' : viewMode === 'preview' ? 'preview' : 'live'}
                    height={viewMode === 'split' ? 520 : 420}
                    hideToolbar={viewMode === 'preview'}
                    textareaProps={{ placeholder: '**Жирный**, _курсив_, __подчёркнутый__, [ссылка](https://t.me/...)\n• Список\n> Цитата\n`код`' }}
                    commands={[
                      commands.bold, commands.italic, commands.strikethrough,
                      commands.link, commands.quote, commands.code, commands.codeBlock,
                    ]}
                    extraCommands={[]}
                    visibleDragbar={false}
                  />
                </Box>
              </Grid>

              <Grid size={6}>
                <TextField label="Мин. задержка (сек)" type="number" {...validation.fieldProps('minDelay', form, update)} fullWidth />
              </Grid>
              <Grid size={6}>
                <TextField label="Макс. задержка (сек)" type="number" {...validation.fieldProps('maxDelay', form, update)} fullWidth />
              </Grid>

              <Grid size={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ justifyContent: 'space-between', py: 1.5 }}
                >
                  Расширенные настройки {showAdvanced ? '' : '(настройте безопасность)'}
                </Button>
              </Grid>

              <Grid size={12}>
                <Collapse in={showAdvanced}>
                  <Box sx={{ pl: 2, pt: 2, borderLeft: '4px solid #DBEAFE', bgcolor: '#F8FAFC', borderRadius: 1, p: 3 }}>
                    <Grid container spacing={3}>
                      <Grid size={6}>
                        <TextField label="Сообщений в час" type="number" {...validation.fieldProps('messagesPerHour', form, update)} fullWidth />
                      </Grid>
                      <Grid size={6}>
                        <TextField label="Суточный лимит" type="number" {...validation.fieldProps('dailyLimitPerAccount', form, update)} fullWidth />
                      </Grid>
                      <Grid size={6}>
                        <TextField label="Пауза каждый час (сек)" type="number" {...validation.fieldProps('hourlyPause', form, update)} fullWidth />
                      </Grid>
                      <Grid size={6}>
                        <TextField label="Задержка при смене аккаунта (сек)" type="number" {...validation.fieldProps('accountSwitchDelay', form, update)} fullWidth />
                      </Grid>
                      <Grid size={6}>
                        <TextField label="Макс. попыток отправки" type="number" {...validation.fieldProps('maxRetries', form, update)} fullWidth />
                      </Grid>
                      <Grid size={6}>
                        <TextField label="Сохранять каждые N сообщений" type="number" {...validation.fieldProps('saveInterval', form, update)} fullWidth />
                      </Grid>

                      <Grid size={12}><Divider sx={{ my: 2 }} /></Grid>

                      <Grid size={12}>
                        <FormControlLabel
                          control={<Checkbox checked={form.randomizeMessage} onChange={e => update('randomizeMessage', e.target.checked)} />}
                          label="Рандомизировать текст сообщения (антиспам)"
                        />
                        <FormControlLabel
                            control={
                              <Checkbox
                                  checked={form.aiRandomizeMessage}
                                  onChange={e => update('aiRandomizeMessage', e.target.checked)}
                              />
                            }
                            label="AI-рандомизация текста (уникальные сообщения)"
                        />
                      </Grid>
                      <Grid size={12}>
                        <FormControlLabel
                          control={<Checkbox checked={form.useParallelAccounts} onChange={e => update('useParallelAccounts', e.target.checked)} />}
                          label="Параллельная отправка (быстрее, но рискованнее)"
                        />
                      </Grid>
                      {form.useParallelAccounts && (
                        <Grid size={12}>
                          <TextField label="Макс. параллельных аккаунтов" type="number" {...validation.fieldProps('maxParallelAccounts', form, update)} fullWidth />
                        </Grid>
                      )}
                      <Grid size={12}>
                        <TextField
                          label="Пропускать при долгом FloodWait (сек)"
                          type="number"
                          {...validation.fieldProps('skipOnLongFlood', form, update)}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Grid>

              {/* Дополнительные функции */}
              <Grid size={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setShowExtraFeatures(!showExtraFeatures)}
                  endIcon={showExtraFeatures ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ justifyContent: 'space-between', py: 1.5 }}
                >
                  Дополнительные режимы и функции {showExtraFeatures ? '' : '(автоответ, пересылка, автоудаление)'}
                </Button>
              </Grid>

              <Grid size={12}>
                <Collapse in={showExtraFeatures}>
                  <Box sx={{ pl: 2, pt: 2, borderLeft: '4px solid #DBEAFE', bgcolor: '#F8FAFC', borderRadius: 1, p: 3 }}>
                    <Grid container spacing={3}>
                      {/* Режим рассылки */}
                      <Grid size={12}>
                        <FormControl fullWidth>
                          <InputLabel id="mode-label">Режим рассылки</InputLabel>
                          <Select
                            labelId="mode-label"
                            label="Режим рассылки"
                            value={form.mode}
                            onChange={e => update('mode', e.target.value as string)}
                          >
                            <MenuItem value="message">Обычная рассылка</MenuItem>
                            <MenuItem value="auto_delete_open">Автоудаление при открытии</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Auto delete TTL */}
                      <Grid size={12}>
                        <TextField
                          label="Автоудаление сообщений через (сек)"
                          type="number"
                          {...validation.fieldProps('autoDeleteTtl', form, update)}
                          fullWidth
                        />
                      </Grid>

                      {/* Trigger Reply + listen_timeout */}
                      <Grid size={12}>
                        <Typography variant="subtitle1" gutterBottom sx={{ color: '#1E40AF' }}>
                          Автоответ на триггер-слова
                        </Typography>
                        <TextField
                          label="Триггер-слова (через запятую)"
                          value={form.triggerWords}
                          onChange={e => update('triggerWords', e.target.value)}
                          fullWidth
                          placeholder="цена, подробнее, интересно, сколько стоит"
                          helperText="Любое совпадение → отправляется автоответ"
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          label="Фиксированный текст ответа"
                          value={form.fixedReply}
                          onChange={e => update('fixedReply', e.target.value)}
                          fullWidth
                          multiline
                          rows={2}
                          placeholder="Спасибо за интерес! Вот цена: 1000 руб."
                          sx={{ mb: 2 }}
                        />
                        <FormControlLabel
                          control={<Checkbox checked={form.useAiReply} onChange={e => update('useAiReply', e.target.checked)} />}
                          label="Использовать AI для генерации ответа (если нет фиксированного текста)"
                        />
                        {form.useAiReply && (
                          <>
                            <TextField
                              label="Системный промпт для AI"
                              value={form.aiSystemPrompt}
                              onChange={e => update('aiSystemPrompt', e.target.value)}
                              fullWidth
                              multiline
                              rows={3}
                              placeholder="Ты вежливый продавец. Отвечай кратко, дружелюбно, предлагай купить."
                              sx={{ mb: 2, mt: 2 }}
                            />
                            <TextField
                              label="Тон ответа AI"
                              value={form.aiTone}
                              onChange={e => update('aiTone', e.target.value)}
                              fullWidth
                              placeholder="дружелюбный, продающий, формальный"
                              sx={{ mb: 2 }}
                            />
                          </>
                        )}
                        <Grid container spacing={2}>
                          <Grid size={6}>
                            <TextField
                              label="Мин. задержка ответа (сек)"
                              type="number"
                              {...validation.fieldProps('replyDelayMin', form, update)}
                              fullWidth
                            />
                          </Grid>
                          <Grid size={6}>
                            <TextField
                              label="Макс. задержка ответа (сек)"
                              type="number"
                              {...validation.fieldProps('replyDelayMax', form, update)}
                              fullWidth
                            />
                          </Grid>
                          <Grid size={6}>
                            <FormControl fullWidth>
                              <InputLabel id="listen-timeout-label">
                                Слушать входящие
                              </InputLabel>
                              <Select
                                  labelId="listen-timeout-label"
                                  label="Слушать входящие"
                                  value={form.listenTimeout}
                                  onChange={e => update('listenTimeout', e.target.value)}
                              >
                                <MenuItem value="0">Не слушать</MenuItem>
                                <MenuItem value="86400">1 день</MenuItem>
                                <MenuItem value="604800">7 дней</MenuItem>
                                <MenuItem value="2678400">31 день</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={6}>
                            <TextField
                              label="Макс. ответов на одного пользователя"
                              type="number"
                              {...validation.fieldProps('maxRepliesPerUser', form, update)}
                              fullWidth
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Forward */}
                      <Grid size={12}>
                        <Typography variant="subtitle1" gutterBottom sx={{ color: '#1E40AF' }}>
                          Пересылка входящих сообщений
                        </Typography>
                        <TextField
                          label="Куда пересылать (@username или chat_id)"
                          value={form.forwardTo}
                          onChange={e => update('forwardTo', e.target.value)}
                          fullWidth
                          placeholder="@mychannel или -1001234567890"
                          sx={{ mb: 2 }}
                        />
                        <FormControlLabel
                          control={<Checkbox checked={form.forwardAsForward} onChange={e => update('forwardAsForward', e.target.checked)} />}
                          label="Пересылать как forward (сохранять источник)"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={form.forwardAddCaption} onChange={e => update('forwardAddCaption', e.target.checked)} />}
                          label="Добавить подпись (От: @username | Аккаунт: имя)"
                        />
                      </Grid>
                      <Grid size={12}>
                        <Typography variant="subtitle1" gutterBottom sx={{color: '#1E40AF'}}>
                          Закрепление сообщения
                        </Typography>

                        <TextField
                            label="Закрепить через (сек)"
                            type="number"
                            {...validation.fieldProps('pinDelaySeconds', form, update)}
                            fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Grid>

              <Grid size={12}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={startBroadcast}
                  disabled={
                    isLoading ||
                    !form.accountNames.length ||
                    (!form.message.trim() && !voiceFile) ||
                    (!recipientsFile && !form.recipientInput.trim() && recipientObjects.length === 0) ||
                    validation.hasErrors
                  }
                  startIcon={isLoading ? <CircularProgress size={24} /> : <PlayArrowIcon />}
                  fullWidth
                  sx={{ py: 2, fontSize: '1.2rem', bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
                >
                  {isLoading ? 'Запуск рассылки...' : 'Запустить'}
                </Button>
              </Grid>
            </Grid>
          )}

          {tab === 'active' && (
            activeBroadcasts.length === 0 ? (
              <Typography textAlign="center" py={10} color="text.secondary">
                Нет активных или остановленных рассылок
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>ID</strong></TableCell>
                      <TableCell><strong>Аккаунтов</strong></TableCell>
                      <TableCell><strong>Получателей</strong></TableCell>
                      <TableCell><strong>Отправлено</strong></TableCell>
                      <TableCell><strong>Ошибок</strong></TableCell>
                      <TableCell><strong>Статус</strong></TableCell>
                      <TableCell><strong>Прогресс</strong></TableCell>
                      <TableCell align="right"><strong>Действия</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeBroadcasts.map(b => (
                      <TableRow
                        key={b.task_id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/mass_sending/detail/${b.task_id}`)}
                      >
                        <TableCell sx={{ fontFamily: 'monospace' }}>{b.task_id.slice(0, 8)}</TableCell>
                        <TableCell>{b.accounts_count}</TableCell>
                        <TableCell>{b.recipients_count}</TableCell>
                        <TableCell sx={{ color: '#10B981', fontWeight: 600 }}>{b.progress.sent}</TableCell>
                        <TableCell sx={{ color: b.progress.failed > 0 ? '#DC2626' : 'inherit' }}>{b.progress.failed}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              b.status === 'running' ? 'Выполняется' :
                              b.status === 'completed' ? 'Завершена' :
                              b.status === 'listening' ? 'На прослушивании ответов' :
                              b.status === 'stopped' ? 'Остановлена' : b.status
                            }
                            size="small"
                            color={
                              b.status === 'running' ? 'success' :
                              b.status === 'listening' ? 'warning':
                              b.status === 'completed' ? 'success' :
                              b.status === 'stopped' ? 'warning' : 'error'
                            }
                            sx={{ minWidth: 90 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flex: 1, bgcolor: '#e5e7eb', borderRadius: 1, height: 8 }}>
                              <Box sx={{ width: `${b.progress.percentage}%`, bgcolor: '#10B981', height: '100%', borderRadius: 1 }} />
                            </Box>
                            <span>{b.progress.percentage.toFixed(0)}%</span>
                          </Box>
                        </TableCell>
                        <TableCell align="right" onClick={e => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            {b.status === 'stopped' && (
                              <IconButton size="small" color="success" onClick={() => restoreBroadcast(b.task_id)} disabled={isLoading}>
                                <PlayArrowIcon fontSize="small" />
                              </IconButton>
                            )}
                            {(b.status === 'running' || b.status ==='listening') && (
                              <IconButton size="small" color="error" onClick={() => stopBroadcast(b.task_id)} disabled={isLoading}>
                                <StopIcon fontSize="small" />
                              </IconButton>
                            )}
                            <IconButton size="small" color="error" onClick={() => deleteBroadcast(b.task_id)} disabled={isLoading}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default MassSending;